// Which ride this browser is recording right now (ADR 0028), so the app opening in another tab does not offer a ride
// that is still going on as an interrupted one. The recording page beats every few seconds; when it goes away it says
// so. Only the recording's id is kept, and a sign-out clears it with the rest of the account's local data.
import { onAuthChange } from './api.js';
import { UPLOAD_EVERY_MS } from './recordings.js';

const KEY = 'gv-live-ride';
/** A page that records beats this often; a beat older than FRESH_MS is a page that died without saying so (a crash). */
export const BEAT_MS = 3000;
export const FRESH_MS = 10_000;

interface Beat { id: string; at: number; gone: boolean }

function read(): Beat | null {
  try {
    const b = JSON.parse(localStorage.getItem(KEY) || 'null') as Beat | null;
    return b && typeof b.id === 'string' && typeof b.at === 'number' ? b : null;
  } catch { return null; }
}
function write(b: Beat | null) { try { if (b) localStorage.setItem(KEY, JSON.stringify(b)); else localStorage.removeItem(KEY); } catch { /* private mode */ } }

/** The recording page is alive. */
export const beat = (id: string, now = Date.now()) => write({ id, at: now, gone: false });
/** The recording page is going away (closed, reloaded): its ride may be picked up at once. */
export const gone = (id: string, now = Date.now()) => write({ id, at: now, gone: true });
/** The ride ended normally: nothing is left to say about it. */
export const ended = (id: string) => { if (read()?.id === id) write(null); };

/**
 * What this browser knows of the recording `id`: `live` while a page of it still records it, `left` when such a page
 * went away (or died), `unknown` when it was never recorded here (another device, or the browser's data was cleared).
 */
export function liveState(id: string, now = Date.now()): 'live' | 'left' | 'unknown' {
  const b = read();
  if (!b || b.id !== id) return 'unknown';
  return !b.gone && now - b.at < FRESH_MS ? 'live' : 'left';
}

/** A recording this browser never rode may be live on another device: offered once it has been quiet past an upload. */
export const QUIET_MS = UPLOAD_EVERY_MS + 30_000;

/**
 * Whether an open recording is an interrupted ride to offer: yes when a page of this browser rode it and went away;
 * not yet while one still beats (another tab rides it); for a ride this browser never saw, only once it went quiet.
 */
export function verdict(rec: { id: string; updatedAt: number }, now = Date.now()): 'offer' | 'wait' | 'skip' {
  const st = liveState(rec.id, now);
  return st === 'live' ? 'wait' : st === 'left' || now - rec.updatedAt > QUIET_MS ? 'offer' : 'skip';
}

if (typeof window !== 'undefined') onAuthChange(s => { if (!s) write(null); });
